import { useEffect, useRef, useState } from 'react';
import { TICK_DURATION, TICKS_TO_NEXT_PLACEHOLDER } from './constants.tsx';
import type { State } from './types.tsx';

export const useAnimatedPlaceholder = (
	placeholder: string | string[],
	isVisible: boolean,
): string => {
	const defaultPlaceholder = Array.isArray(placeholder) ? placeholder[0] : placeholder;
	const [state, setState] = useState<State>({
		placeholderIndex: 0,
		ticksInCurrentPlaceholder: 0,
		visiblePlaceholder: defaultPlaceholder,
	});

	const interval = useRef<ReturnType<typeof setInterval>>();

	useEffect(() => {
		// If any of the hook dependencies (placeholder visibility or project key) changes:
		// - Clear any previously created intervals
		// - Reset state back to initial
		clearInterval(interval.current);
		setState({
			placeholderIndex: 0,
			ticksInCurrentPlaceholder: 0,
			visiblePlaceholder: defaultPlaceholder,
		});

		// Only animate when a series of placeholders are provided
		if (isVisible && Array.isArray(placeholder) && placeholder.length > 1) {
			interval.current = setInterval(() => {
				setState(({ placeholderIndex, ticksInCurrentPlaceholder, visiblePlaceholder }: State) => {
					const targetPlaceholder = placeholder[placeholderIndex];

					if (visiblePlaceholder !== targetPlaceholder) {
						return {
							placeholderIndex,
							ticksInCurrentPlaceholder: 0,
							visiblePlaceholder: targetPlaceholder.slice(0, visiblePlaceholder.length + 1),
						};
					}

					if (ticksInCurrentPlaceholder === TICKS_TO_NEXT_PLACEHOLDER) {
						return {
							placeholderIndex:
								placeholderIndex < placeholder.length - 1 ? placeholderIndex + 1 : 0,
							ticksInCurrentPlaceholder: 0,
							visiblePlaceholder: '',
						};
					}

					return {
						placeholderIndex,
						ticksInCurrentPlaceholder: ticksInCurrentPlaceholder + 1,
						visiblePlaceholder,
					};
				});
			}, TICK_DURATION);
		}

		return () => clearInterval(interval.current);
	}, [defaultPlaceholder, isVisible, placeholder]);

	return state.visiblePlaceholder;
};
